import { useEffect, useState } from "react";
import { useLanguage } from "../../../../contexts/LanguageContext";
import useFirebaseAboutHooks from "./aboutHooks";

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { language } = useLanguage();
  const {
    title,
    titleHighlight,
    subTitletext,
    statsData,
    storyTitle,
    storyText1,
    storyText2,
    experienceData,
  } = useFirebaseAboutHooks(language);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 },
    );

    const element = document.getElementById("about");
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  return (
    <section id="about" className="bg-ink py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className={`transition-all duration-700 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}
        >
          <div className="mb-16 max-w-3xl">
            <h2 className="mb-4 font-display text-4xl font-bold tracking-tight text-fg md:text-5xl">
              {title.data ?? "About"}{" "}
              <span className="text-accent">{titleHighlight.data ?? ""}</span>
            </h2>
            <p className="text-xl text-muted">{subTitletext.data ?? ""}</p>
          </div>

          <div className="mb-20 grid grid-cols-1 items-start gap-14 lg:grid-cols-2">
            <div>
              <h3 className="mb-4 font-display text-2xl font-bold text-fg">
                {storyTitle.data}
              </h3>
              <p className="mb-6 leading-relaxed text-muted">
                {new String(storyText1.data).replace(
                  "[years]",
                  (new Date().getFullYear() - 2018).toString(),
                )}
              </p>
              <p className="leading-relaxed text-muted">{storyText2.data}</p>
            </div>

            <div className="grid grid-cols-2 gap-6 border-t border-white/10 pt-6">
              {statsData.data?.map((stat: any, index: number) => (
                <div key={index} className="space-y-1">
                  <div className="font-display text-3xl font-bold text-highlight">
                    {stat.value.includes("[years]")
                      ? new Date().getFullYear() - 2018
                      : stat.value}
                  </div>
                  <div className="text-sm text-muted">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="absolute bottom-0 left-4 top-0 w-px bg-accent/50 md:left-1/2 md:-translate-x-1/2" />
            <div className="space-y-12">
              {experienceData.data?.map((experience: any, index: number) => (
                <div
                  key={index}
                  className={
                    "relative flex items-center " +
                    (index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse")
                  }
                >
                  <div className="absolute left-4 z-10 h-3 w-3 -translate-x-1/2 border-2 border-ink bg-accent md:left-1/2" />
                  <div
                    className={
                      "ml-12 md:ml-0 md:w-1/2 " +
                      (index % 2 === 0 ? "md:pr-10" : "md:pl-10")
                    }
                  >
                    <div className="border border-white/10 bg-surface p-6">
                      <div className="mb-3 flex items-center justify-between gap-4">
                        <span className="font-display text-lg font-bold text-accent">
                          {experience.period}
                        </span>
                        <span className="text-sm text-muted">
                          {experience.company}
                        </span>
                      </div>
                      <h4 className="mb-3 font-display text-xl font-bold text-fg">
                        {experience.title}
                      </h4>
                      <p className="mb-4 text-muted">{experience.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {experience.stack?.map((stack: string, i: number) => (
                          <span
                            key={i}
                            className="border border-highlight/40 px-3 py-1 text-sm text-highlight"
                          >
                            {stack}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { About };
